import { useGetList, useTranslate } from "ra-core";
import { useMemo } from "react";

import type { DealSource, DisqualifyReason } from "../types";

// Kildene til en mulighet (deals.source, kap. 6.1). Kolonnen er fri tekst;
// dette er verdiene skjermene kjenner. Ukjente verdier vises som de er.
export const DEAL_SOURCES: DealSource[] = [
  "manual",
  "prospect",
  "web_form",
  "email",
  "phone",
  "referral",
  "upsell",
  "other",
];

export const DEAL_SOURCE_FALLBACK: Record<DealSource, string> = {
  manual: "Manuell",
  prospect: "Prospektering",
  web_form: "Nettskjema",
  email: "E-post",
  phone: "Telefon",
  referral: "Anbefaling",
  upsell: "Eksisterende kunde (mersalg)",
  other: "Annet",
};

// Kildene vi kan velge i hurtigregistreringen. Nettskjema, prospektering og
// mersalg settes av systemet.
export const QUICK_LEAD_SOURCES: DealSource[] = [
  "phone",
  "referral",
  "email",
  "other",
];

export const useDealSourceLabel = () => {
  const translate = useTranslate();
  return (source: string | null | undefined) => {
    if (!source) return "";
    const fallback = DEAL_SOURCE_FALLBACK[source as DealSource];
    return fallback
      ? translate(`crm.deals.lead.source.${source}`, { _: fallback })
      : source;
  };
};

// Diskvalifiseringsårsaker (tabellen disqualify_reasons, kap. 6.7). Dialogen
// viser bare de synlige, i rekkefølge; skjulte trengs for etiketten.
export const NOT_A_LEAD_REASON = "ikke_henvendelse";

const NO_REASONS: DisqualifyReason[] = [];

export const useDisqualifyReasons = (enabled = true) => {
  const { data = NO_REASONS, isPending } = useGetList<DisqualifyReason>(
    "disqualify_reasons",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "position", order: "ASC" },
      filter: {},
    },
    { staleTime: 60_000, enabled },
  );
  const visible = useMemo(
    () =>
      data
        .filter((r) => !r.hidden)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [data],
  );
  const byValue = useMemo(() => new Map(data.map((r) => [r.value, r])), [data]);
  return { reasons: data, visible, byValue, isPending };
};

export const useDisqualifyReasonLabel = () => {
  const { byValue } = useDisqualifyReasons();
  return (value?: string | null) =>
    value ? (byValue.get(value)?.label ?? value) : "";
};
